import { Link } from 'react-router-dom';
import { categories, seoArticles } from '@/mocks/categories';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://oopbuyspread.wiki';

const dbJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'OOPBuy Spreadsheet Database',
  url: `${SITE_URL}/spreadsheet-database`,
  description:
    'Durchsuche die aktuellsten Spreadsheets, entdecke trendige Produkte und bleibe mit wöchentlichen Updates immer auf dem neuesten Stand.',
  isPartOf: {
    '@type': 'WebSite',
    name: 'OOPBuy Spreadsheet',
    url: SITE_URL,
  },
  inLanguage: 'de',
};

const trendingProducts = [
  { name: 'Nike Dunk Low Panda', category: 'Sneaker', rating: '★★★★★', link: 'https://kaspreadsheet.com/category-911-Shoes.html' },
  { name: 'Essentials Hoodie', category: 'Hoodies', rating: '★★★★★', link: 'https://kaspreadsheet.com/search-912/name/Shoes.html' },
  { name: 'Gallery Dept T-Shirt', category: 'T-Shirts', rating: '★★★★☆', link: 'https://kaspreadsheet.com/search-913/name/Shoes.html' },
  { name: 'Arc\'teryx Beta Jacket', category: 'Jackets', rating: '★★★★★', link: 'https://kaspreadsheet.com/search-914/name/Shoes.html' },
  { name: 'Stone Island Cargo Pants', category: 'Pants', rating: '★★★★☆', link: 'https://kaspreadsheet.com/search-915/name/Shoes.html' },
  { name: 'New Era 59Fifty Cap', category: 'Headwear', rating: '★★★★☆', link: 'https://kaspreadsheet.com/search-916/name/Shoes.html' },
  { name: 'Louis Vuitton Bag Tribute', category: 'Bags', rating: '★★★★☆', link: 'https://kaspreadsheet.com/search-921/name/Shoes.html' },
  { name: 'Hermès Belt Replica', category: 'Belts', rating: '★★★★☆', link: 'https://kaspreadsheet.com/search-922/name/Shoes.html' },
];

const updates = [
  { date: '14. Juli 2026', text: 'Neue Sneaker-Kategorie hinzugefügt – über 200 neue Jordan & Dunk Links' },
  { date: '12. Juli 2026', text: 'QC-Foto Datenbank erweitert: 50+ neue Verkäufer mit Bildern' },
  { date: '10. Juli 2026', text: 'Versandkosten-Rechner für Deutschland, Österreich & Schweiz aktualisiert' },
  { date: '08. Juli 2026', text: 'Neue Kategorie "Accessoires" um 100+ Produkte erweitert' },
  { date: '05. Juli 2026', text: 'Sicherheits-Guide überarbeitet – neue Betrugswarnungen hinzugefügt' },
  { date: '02. Juli 2026', text: 'Spreadsheet-Struktur optimiert: schnellere Navigation & bessere Filter' },
];

export default function SpreadsheetDatabase() {
  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(dbJsonLd) }}
      />
      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="w-full px-6 md:px-10">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="font-heading text-xl font-bold text-foreground-900 tracking-tight">
              oopbuy<span className="text-primary-500">spread</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/" className="text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap">
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line"></i></span>
                  Startseite
                </span>
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-4 py-2 text-sm font-semibold bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      </nav>

      <main>
        <section className="relative w-full py-20 md:py-24 px-6 md:px-10 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20data%20dashboard%20interface%20design%20on%20laptop%20screen%20surrounded%20by%20fashion%20products%20sneakers%20and%20accessories%20on%20clean%20wooden%20desk%20soft%20natural%20lighting%20organized%20workspace%20aesthetic%20warm%20neutral%20tones%20editorial%20tech%20lifestyle%20composition&width=1920&height=900&seq=db-hero&orientation=landscape"
            alt="OOPBuy Spreadsheet Database"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/45"></div>
          <div className="relative z-10 max-w-6xl mx-auto text-center">
            <span className="inline-block px-4 py-1.5 mb-5 text-xs font-semibold tracking-wider uppercase bg-accent-500/90 text-background-50 rounded-full">
              Database
            </span>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-4">
              OOPBuy Spreadsheet Database
            </h1>
            <p className="text-sm md:text-lg text-background-200/90 max-w-2xl mx-auto">
              Durchsuche die aktuellsten Spreadsheets, entdecke trendige Produkte und bleibe mit wöchentlichen Updates immer auf dem neuesten Stand.
            </p>
          </div>
        </section>

        <section className="w-full py-14 md:py-18 px-6 md:px-10 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                <i className="ri-fire-line"></i>
              </span>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                Trending Produkte
              </h2>
            </div>

            <div className="overflow-x-auto rounded-lg border border-background-200/70">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-secondary-100">
                    <th className="px-5 py-3.5 text-sm font-semibold text-foreground-800">Produkt</th>
                    <th className="px-5 py-3.5 text-sm font-semibold text-foreground-800">Kategorie</th>
                    <th className="px-5 py-3.5 text-sm font-semibold text-foreground-800">Bewertung</th>
                    <th className="px-5 py-3.5 text-sm font-semibold text-foreground-800">Link</th>
                  </tr>
                </thead>
                <tbody>
                  {trendingProducts.map((prod, i) => (
                    <tr key={i} className="border-b border-background-200/50 bg-background-50 hover:bg-background-100/50 transition-colors">
                      <td className="px-5 py-3.5 text-sm font-medium text-foreground-800">{prod.name}</td>
                      <td className="px-5 py-3.5">
                        <span className="inline-block px-2 py-0.5 text-xs bg-secondary-100 text-secondary-700 rounded-full">{prod.category}</span>
                      </td>
                      <td className="px-5 py-3.5 text-sm text-accent-600">{prod.rating}</td>
                      <td className="px-5 py-3.5">
                        <a
                          href={prod.link}
                          target="_blank"
                          rel="noopener nofollow"
                          className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
                        >
                          Öffnen
                          <span className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-external-link-line text-xs"></i>
                          </span>
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="w-full py-14 md:py-18 px-6 md:px-10 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                <i className="ri-refresh-line"></i>
              </span>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                Wöchentliche Updates
              </h2>
            </div>

            <div className="space-y-4">
              {updates.map((update, i) => (
                <div key={i} className="flex items-start gap-4 p-4 md:p-5 bg-background-50 border border-background-200/70 rounded-lg">
                  <span className="inline-block px-3 py-1 text-xs font-semibold bg-accent-100 text-accent-700 rounded-md whitespace-nowrap flex-shrink-0 mt-0.5">
                    {update.date}
                  </span>
                  <p className="text-sm text-foreground-700">{update.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-14 md:py-18 px-6 md:px-10 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                <i className="ri-folder-2-line"></i>
              </span>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                Kategorie Navigation
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {categories.map((cat, i) => (
                <a
                  key={i}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex flex-col items-center gap-2 p-4 bg-background-100 border border-background-200/70 rounded-lg hover:border-primary-300 hover:bg-background-50 transition-all duration-200 cursor-pointer"
                >
                  <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className={cat.icon}></i>
                  </span>
                  <span className="text-xs font-medium text-foreground-700 text-center">
                    {cat.name}
                  </span>
                </a>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-14 md:py-18 px-6 md:px-10 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8 text-center">
              Weiterführende Guides
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {seoArticles.slice(0, 6).map((article, i) => (
                <Link
                  key={i}
                  to={`/${article.slug}`}
                  className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300 transition-all duration-200 cursor-pointer"
                >
                  <div className="relative h-40 overflow-hidden bg-background-200/50">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4">
                    <h4 className="text-sm font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2 mb-1">
                      {article.title}
                    </h4>
                    <p className="text-xs text-foreground-500 line-clamp-2">{article.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <footer className="w-full bg-background-900 text-background-200 py-10 px-6 md:px-10">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="font-heading text-lg font-bold text-background-50">
            oopbuy<span className="text-accent-400">spread</span>
          </Link>
          <p className="text-xs text-background-500">
            &copy; 2026 oopbuyspread.wiki – Unabhängige Informationsplattform
          </p>
        </div>
      </footer>
    </div>
  );
}